import { z } from 'zod';
import { STATE_CODES } from '../india.js';
import { FOOD_TYPES } from '../menu-defaults.js';
import { SUBSCRIPTION_STATUSES } from '../platform.js';
import { gstin, phone, pincode, rupees, shortText } from './common.js';
import { changePasswordSchema } from './identity.js';
/**
 * The business profile a hotelier fills in when they register.
 *
 * Every field here is mandatory on purpose: these are the details that appear
 * on a tax invoice, and a property that cannot raise a compliant invoice is not
 * usable. The email address is part of the profile, not just the login.
 */
export const businessProfileSchema = z.object({
    name: shortText,
    legalName: shortText.optional(),
    email: z.string().trim().toLowerCase().email('Enter the hotel’s email address'),
    phone,
    gstin,
    /** Chosen from the GST state list, not typed: a wrong code means wrong tax. */
    stateCode: z
        .string()
        .refine((code) => STATE_CODES.includes(code), 'Choose the state from the list'),
    address: z.string().trim().min(5, 'Enter the full address').max(240),
    city: shortText,
    /** Printed on every invoice, and required by the e-invoice schema. */
    pincode,
    /** A hotel serving food needs one, and it is printed on the invoice. */
    fssai: z
        .string()
        .trim()
        .regex(/^\d{14}$/, 'An FSSAI licence number is 14 digits'),
    /** Decides which starter menu the new hotel is given. */
    foodType: z.enum(FOOD_TYPES, {
        message: 'Say whether you serve vegetarian, non-vegetarian or both',
    }),
});
export const registrationOwnerSchema = z.object({
    name: shortText,
    email: z.string().trim().toLowerCase().email('Enter a valid email address'),
    password: changePasswordSchema.shape.newPassword,
    phone: z.union([phone, z.literal('')]).optional(),
});
export const registerHotelSchema = z.object({
    business: businessProfileSchema,
    owner: registrationOwnerSchema,
    planCode: z
        .string()
        .trim()
        .toUpperCase()
        .regex(/^[A-Z0-9_-]{2,20}$/)
        .optional(),
    /**
     * Not optional, and not merely a boolean: only `true` parses.
     *
     * Registration is the moment the hotel enters a contract, so the agreement
     * has to be a positive act. What was agreed to, and when, is recorded on the
     * tenant - a tick box nobody keeps evidence of is theatre.
     */
    acceptTerms: z.literal(true, { error: 'Please accept the terms and conditions' }),
    /** Registration is a public write, so it carries a challenge like sign-in. */
    captchaId: z.string().uuid().optional(),
    captchaAnswer: z.string().trim().min(1).max(12).optional(),
    /** The reCAPTCHA token, when that is the challenge in use. */
    captchaToken: z.string().trim().min(1).max(4096).optional(),
    /** Emailed to the business address. Only required once SMTP is configured. */
    otp: z.string().trim().regex(/^\d{4,8}$/, 'Enter the code we emailed you').optional(),
});
/* ---------------- plans ---------------- */
export const planCode = z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9_-]{2,20}$/, 'Use 2-20 letters, digits, hyphen or underscore');
/**
 * Plan fields without defaults.
 *
 * The update schema is built from these rather than from `createPlanSchema`:
 * `.partial()` leaves a field's `.default()` in place, so a partial update that
 * only sent a price would also quietly reset visibility, the active flag, the
 * sort order and the feature list to their defaults.
 */
const planFields = {
    name: shortText,
    description: z.string().trim().max(240).optional().or(z.literal('')),
    priceMonthly: rupees,
    trialDays: z.number().int().min(0).max(365),
    maxProperties: z.number().int().min(1).max(1000),
    maxUsers: z.number().int().min(1).max(10000),
    features: z.array(shortText).max(20),
    isPublic: z.boolean(),
    isActive: z.boolean(),
    sortOrder: z.number().int().min(0).max(999),
};
export const createPlanSchema = z.object({
    code: planCode,
    ...planFields,
    features: z.array(shortText).max(20).default([]),
    isPublic: z.boolean().default(true),
    isActive: z.boolean().default(true),
    sortOrder: z.number().int().min(0).max(999).default(0),
});
export const updatePlanSchema = z.object(planFields).partial();
/* ---------------- subscription administration ---------------- */
export const SUBSCRIPTION_ACTIONS = [
    'CHANGE_PLAN',
    'EXTEND_TRIAL',
    /** Adds months to a paying hotel, from whatever it already has. */
    'EXTEND_PLAN',
    'ACTIVATE',
    'SUSPEND',
    'CANCEL',
];
export const subscriptionActionSchema = z
    .object({
    action: z.enum(SUBSCRIPTION_ACTIONS),
    planCode: planCode.optional(),
    days: z.number().int().min(1).max(365).optional(),
    months: z.number().int().min(1).max(36).optional(),
    note: z.string().trim().max(240).optional().or(z.literal('')),
})
    .refine((v) => v.action !== 'CHANGE_PLAN' || Boolean(v.planCode), {
    message: 'Choose the plan to move this hotel onto',
    path: ['planCode'],
})
    .refine((v) => v.action !== 'EXTEND_PLAN' || typeof v.months === 'number', {
    message: 'Say how many months to add',
    path: ['months'],
})
    .refine((v) => v.action !== 'EXTEND_TRIAL' || typeof v.days === 'number', {
    message: 'Say how many days to add',
    path: ['days'],
});
/* ---------------- platform queries ---------------- */
export const tenantListQuerySchema = z.object({
    q: z.string().trim().max(80).optional(),
    status: z.enum(SUBSCRIPTION_STATUSES).optional(),
    planCode: planCode.optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
    offset: z.coerce.number().int().min(0).default(0),
});
export const platformReportQuerySchema = z.object({
    days: z.coerce.number().int().min(1).max(365).default(30),
});
/* ---------------- outgoing mail ---------------- */
/* ---------------- sign-in challenge ---------------- */
/**
 * Google reCAPTCHA keys, set by a superadmin.
 *
 * A blank secret means "keep the one already saved" - the screen is never sent
 * the secret, so it has nothing to send back.
 */
export const recaptchaSettingsSchema = z.object({
    siteKey: z.string().trim().max(200),
    secretKey: z.string().trim().max(200).optional(),
    isEnabled: z.boolean(),
});
export const smtpSettingsSchema = z.object({
    host: z.string().trim().max(200),
    port: z.number().int().min(1).max(65535),
    secure: z.boolean(),
    username: z.string().trim().max(200).optional().or(z.literal('')),
    /** Blank means keep the stored one: a screen never receives it to send back. */
    password: z.string().max(200).optional().or(z.literal('')),
    fromName: z.string().trim().max(80),
    fromEmail: z.string().trim().toLowerCase().email('Enter the address mail is sent from'),
    isEnabled: z.boolean(),
});
export const testMailSchema = z.object({
    to: z.string().trim().toLowerCase().email('Where should the test go?'),
});
/* ---------------- payment providers ---------------- */
export const PAYMENT_PROVIDER_KINDS = ['RAZORPAY', 'PAYU', 'CASHFREE', 'STRIPE'];
export const paymentProviderEnum = z.enum(PAYMENT_PROVIDER_KINDS);
export const paymentSettingSchema = z.object({
    keyId: z.string().trim().max(200).optional().or(z.literal('')),
    keySecret: z.string().max(400).optional().or(z.literal('')),
    isLive: z.boolean().optional(),
    isEnabled: z.boolean().optional(),
});
export const confirmPaymentSchema = z.object({
    reference: z.string().trim().min(8).max(60),
    providerRef: z.string().trim().min(1).max(120),
    orderId: z.string().trim().min(1).max(120),
    signature: z.string().trim().min(8).max(400),
});
/* ---------------- public room booking ---------------- */
const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Use a date like 2026-08-27');
export const availabilityQuerySchema = z.object({ from: isoDay, to: isoDay });
export const createBookingSchema = z.object({
    roomTypeId: z.string().uuid(),
    from: isoDay,
    to: isoDay,
    guestName: shortText,
    guestPhone: phone,
    guestEmail: z.string().trim().toLowerCase().email().optional().or(z.literal('')),
    adults: z.number().int().min(1).max(10),
    children: z.number().int().min(0).max(10).optional(),
    note: z.string().trim().max(240).optional().or(z.literal('')),
});
/* ---------------- registration OTP ---------------- */
export const requestOtpSchema = z.object({
    email: z.string().trim().toLowerCase().email('Enter the hotel email address'),
    hotelName: z.string().trim().max(120).optional().or(z.literal('')),
});
//# sourceMappingURL=platform.js.map